import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import ShippingModal from 'modules/shipping/components/shipping-modal/ShippingModal';
import SubcampaignOfferDeclineModal from './offer-decline-modal/SubcampaignOfferDeclineModal';
import Input from 'components/inputs/input/Input';

import { putOfferState } from 'modules/subcampaigns/reducers/offer';
import { fetchSubcampaign, sendQuote } from 'modules/subcampaigns/reducers/subcampaign';

import {
  SUBCAMPAIGN_STATE_ACCEPTED,
  isInfluencerActionRequired,
  isNegotiationsReopened,
  isQuote,
  shouldAskForShippingAddress
} from 'utils/subcampaign';
import { t } from 'utils/language';

import type { Subcampaign, SubcampaignOfferState, TextEntry } from 'types';

import type { SyntheticEvent } from 'react';

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: ShippingModal
});

const DeclineButtonWithModal = withModal(ActionButton, {
  modalContent: SubcampaignOfferDeclineModal
});

type OwnProps = {
  className?: string,
  buttonWidth: 'normal' | 'maximum',
  subcampaign: Subcampaign,
  setProvideQuoteClick?: (a: () => void) => void,
  fromMessenger?: boolean
};

type DispatchProps = {
  dispatchPutOfferState: (b: number, a: SubcampaignOfferState) => Promise<void>,
  dispatchFetchSubcampaign: (a: number) => Promise<void>,
  dispatchSendQuote: (b: Subcampaign, a: string) => Promise<void>
};

type State = {
  quote: string,
  error?: string | null
};

type Props = OwnProps & DispatchProps;

class SubcampaignOfferActions extends React.Component<Props, State> {
  state = {
    quote: '',
    error: null
  };

  input: TextEntry | undefined | null = null;

  componentDidMount() {
    if (this.props.setProvideQuoteClick) {
      this.props.setProvideQuoteClick(this.handleProvideQuote);
      this.handleProvideQuote();
    }
  }

  handleAcceptRenegotiatedSubcampaign = (): void => {
    const { subcampaign, dispatchPutOfferState, dispatchFetchSubcampaign } = this.props;

    const offer = subcampaign.updatedOffer || subcampaign.offer;
    dispatchPutOfferState(offer.id, { state: SUBCAMPAIGN_STATE_ACCEPTED })
      .then(() => dispatchFetchSubcampaign(subcampaign.id));
  };

  handleQuoteChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    this.setState({
      quote: value,
      error: null
    });
  };

  handleSendQuote = () => {
    const { subcampaign, dispatchSendQuote } = this.props;
    const { quote } = this.state;

    if (isNaN(Number(quote)) || Number(quote) <= 0) {
      this.setState({
        error: t('quote_mandatory_error')
      });

      return;
    }

    dispatchSendQuote(subcampaign, quote);
  };

  handleProvideQuote = () => {
    if (this.input) {
      this.input.focus();
    }
  };

  renderDeclineButton(subcampaign: Subcampaign): React.ReactNode {
    const { buttonWidth } = this.props;

    return (
      <DeclineButtonWithModal
        width={buttonWidth}
        modalContentProps={{
          subcampaign
        }}
      >
        {
          isNegotiationsReopened(subcampaign)
            ? t('subcampaign_button_decline_renegotiation')
            : t('subcampaign_button_decline')
          }
      </DeclineButtonWithModal>
    );
  }

  renderInput(): React.ReactNode {
    const { subcampaign, fromMessenger } = this.props;
    const { error } = this.state;

    if (isQuote(subcampaign) && !fromMessenger) {
      return (
        <div className="subcampaign-offer-actions__input">
          <Input
            hint="€"
            type="number"
            hintAlign="right"
            value={this.state.quote}
            onChange={this.handleQuoteChange}
            width="responsive"
            align="right"
            refCallback={(ref) => { this.input = ref; }}
            data={{ qa: 'offer-form__amount-input' }}
          />
          {
            error && (
              <div className="subcampaign-qoute-error__container">
                {error}
              </div>
            )
          }
        </div>
      );
    }

    return null;
  }

  renderActions(): React.ReactNode {
    const { subcampaign, buttonWidth, fromMessenger } = this.props;

    const hasRequestedBudget = !isNaN(Number(this.state.quote));

    const actionClasses = classnames({
      'subcampaign-offer-actions__action': true,
      [`subcampaign-offer-actions__action--${buttonWidth}`]: buttonWidth
    });

    if (isQuote(subcampaign)) {
      return (
        <div className={actionClasses}>
          {this.renderDeclineButton(subcampaign)}
          {
            !fromMessenger
              ? (
                <ActionButton
                  color="primary"
                  width={buttonWidth}
                  disabled={!hasRequestedBudget}
                  onClick={() => this.handleSendQuote()}
                >
                  {t('subcampaign_messenger.send_quote')}
                </ActionButton>
              )
              : (
                <ActionButton
                  color="primary"
                  width={buttonWidth}
                  link={`/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}`}
                >
                  {t('subcampaign_messenger.view_brief')}
                </ActionButton>
              )
          }
        </div>
      );
    }

    return (
      <React.Fragment>
        <div className={actionClasses}>
          {
            (isNegotiationsReopened(subcampaign) && !shouldAskForShippingAddress(subcampaign)) ? (
              <ActionButton
                color="primary"
                width={buttonWidth}
                onClick={this.handleAcceptRenegotiatedSubcampaign}
              >
                {t('subcampaign_button_accept')}
              </ActionButton>
            ) : (
              <ActionButtonWithModal
                color="primary"
                width={buttonWidth}
                modalContentProps={{
                  subcampaign
                }}
              >
                {t('subcampaign_button_accept')}
              </ActionButtonWithModal>
            )
          }
        </div>
        <div className={actionClasses}>
          {this.renderDeclineButton(subcampaign)}
        </div>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { className, subcampaign, buttonWidth, fromMessenger } = this.props;
    const isQuoted = isQuote(subcampaign);

    // we hide the offer action buttons instead of removing them from the DOM because these buttons
    // launch a modal via HOC. if we were to remove the buttons from DOM, the modal would disappear
    const classes = classnames({
      'subcampaign-offer-actions': true,
      'subcampaign-offer-actions--visible': isInfluencerActionRequired(subcampaign, subcampaign.campaign),
      [className || '']: Boolean(className)
    });

    const actionsClasses = classnames({
      'subcampaign-offer-actions__actions': true,
      [`subcampaign-offer-actions__actions--direction-${buttonWidth === 'maximum' ? 'column' : 'row'}`]: buttonWidth
    });

    let title = isQuoted && !fromMessenger
      ? t('subcampaign_messenger.quote_suggestion')
      : null;

    title = !isQuoted ? t('subcampaign_messenger.offer_suggestion') : title;

    if (isNegotiationsReopened(subcampaign)) {
      title = t('subcampaign_messenger.renegotiation_opened');
    }

    return (
      <section className={classes}>
        {
          title &&
          <div className="subcampaign-offer-actions__title">
            {title}
          </div>
        }
        {this.renderInput()}
        <div className={actionsClasses}>
          {this.renderActions()}
        </div>
      </section>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchPutOfferState: (offerId: number, state: SubcampaignOfferState): Promise<void> => dispatch(putOfferState(offerId, state)),
  dispatchFetchSubcampaign: (subcampaignId: number): Promise<void> => dispatch(fetchSubcampaign(subcampaignId)),
  dispatchSendQuote: (subcampaign: Subcampaign, quote: string): Promise<void> => dispatch(sendQuote(subcampaign, quote))
});

export default connect(null, mapDispatchToProps)(SubcampaignOfferActions);
